import { useRouter } from 'next/router';
import { striptags } from 'striptags';

import * as graphqlTypes from '@/types/graphql';
import { ExcerptPost } from '@/ui/organisms/ExcerptPostList';
import Template, { TagInfo } from '@/ui/templates/Keyword';
import Head from '@/ui/utils/Head';
import * as graphql from '@/utils/graphql';
import { mapNullableImageSrcToCmsCdn } from '@/utils/image-src-cms-map';
import {
  getSharedProps,
  SharedProps,
  SharedPropsProvider,
} from '@/utils/shared';
import { getTagOverrides } from '@/utils/tag-overrides';
import { getTagRelatedTags, getTagVimeoId } from '@/utils/tagInfo';

import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';

type ServerProps = SharedProps & {
  totalPage: number;
  currentPage: number;
  specialPosts: ExcerptPost[];
  popularPosts: ExcerptPost[];
  tagInfo: TagInfo;
};

type Params = {
  keyword: string;
  pageNum?: string;
};

const Page: NextPage<ServerProps> = ({
  totalPage,
  currentPage,
  popularPosts,
  tagInfo,
  ...sharedProps
}) => {
  const router = useRouter();

  const {
    headDescription = tagInfo.description,
    title = `${tagInfo.name}に関する卓球レッスン動画 ${tagInfo.count}本`,
    headTitle = `「${tagInfo.name}に関する動画 - 卓球レッスン動画／動画でまなぶ卓球 シェークハンズ`,
  } = getTagOverrides(tagInfo.slug);

  return (
    <SharedPropsProvider value={sharedProps}>
      <Head
        title={headTitle}
        description={headDescription}
        keywords={['卓球', '卓球動画', '卓球情報']}
      />
      <Template
        title={title}
        tagInfo={tagInfo}
        url={router.asPath}
        totalPage={totalPage}
        currentPage={currentPage}
        popularPosts={popularPosts}
      />
    </SharedPropsProvider>
  );
};

export default Page;

const query = /* GraphQL */ `
  query getKeywordData(
    $offset: Int!
    $postsPerPage: Int!
    $tagID: ID!
    $tag: String! # $keywords: [String]!
  ) {
    tag(id: $tagID, idType: SLUG) {
      slug
      name
      description
    }
    totalTagPosts: getTotalPostsWithViewCount(tag: $tag)
    popularPosts: listPopularShPostsInTag(
      postsPerPage: $postsPerPage
      offset: $offset
      tag: $tag
    ) {
      id
      title
      date
      excerpt
      imageUrl
    }
    specialPosts: posts(
      first: 3
      where: {
        status: PUBLISH
        orderby: [{ field: DATE, order: DESC }]
        taxQuery: {
          taxArray: {
            field: SLUG
            taxonomy: TAG
            operator: AND
            terms: [$tag, "tokusyu_date"]
          }
        }
      }
    ) {
      nodes {
        databaseId
        title
        date
        excerpt
        featuredImage {
          node {
            sourceUrl
          }
        }
      }
    }
  }
`;

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<ServerProps, Params> = async ({
  params,
}) => {
  const { keyword, pageNum } = params as Params;
  const page = +(pageNum ?? 1);
  const postsPerPage = 20;

  // 1ページ目の場合、 /page/1 は省略する
  if (typeof pageNum !== 'undefined' && +pageNum === 1) {
    return {
      redirect: {
        statusCode: 302,
        destination: `/keyword/${keyword}`,
      },
    };
  }

  const [sharedProps, response] = await Promise.all([
    getSharedProps(),
    graphql.query<
      graphqlTypes.GetKeywordDataQuery,
      graphqlTypes.GetKeywordDataQueryVariables
    >({
      query,
      variables: {
        offset: postsPerPage * (page - 1),
        postsPerPage: postsPerPage,
        tagID: keyword,
        tag: keyword,
      },
    }),
  ]);

  const totalPage = Math.ceil(response.totalTagPosts / postsPerPage);

  // 存在するページ範囲以外のページが指定された場合;
  if (page < 1 || totalPage < page) {
    return {
      notFound: true,
    };
  }

  if (!response.tag) {
    return {
      notFound: true,
    };
  }

  const vimeoId = getTagVimeoId(keyword);
  const relatedTags = getTagRelatedTags(keyword);

  return {
    props: {
      ...sharedProps,
      tagInfo: {
        slug: response.tag?.slug ?? '',
        name: response.tag?.name ?? '',
        description: response.tag?.description ?? '',
        count: response.totalTagPosts,
        ...(vimeoId ? { vimeoId: vimeoId } : {}),
        ...(relatedTags ? { relatedTags: relatedTags } : {}),
      },
      specialPosts: mapSpecialPosts(response.specialPosts),
      popularPosts: mapPopularPosts(response.popularPosts),
      currentPage: page,
      totalPage,
    },
    revalidate: 3600,
  };
};

const mapPopularPosts = (
  posts: graphqlTypes.GetKeywordDataQuery['popularPosts'],
): ExcerptPost[] =>
  posts.map(node => ({
    id: Number(node.id),
    title: node.title,
    date: node.date,
    excerpt: node.excerpt ?? '',
    imageUrl: mapNullableImageSrcToCmsCdn(node.imageUrl),
  }));

const mapSpecialPosts = (
  posts: graphqlTypes.GetKeywordDataQuery['specialPosts'],
): ExcerptPost[] =>
  posts?.nodes?.map(node => ({
    id: node?.databaseId ?? 0,
    title: node?.title ?? '',
    date: node?.date ?? '2020-01-01T12:00:00',
    imageUrl: mapNullableImageSrcToCmsCdn(node?.featuredImage?.node?.sourceUrl),
    excerpt: striptags(node?.excerpt ?? ''),
  })) ?? [];
